"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import {
  MessageCircle,
  Phone,
  Mail,
  Clock,
  User,
  MessageSquare,
  MapPin,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

function ContactHero() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [debugInfo, setDebugInfo] = useState("");

  // Your Google Apps Script web app URL
  const GOOGLE_SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbx78Y8WRIyWyufP0Awn6hFMI6GXVVhPotIncuIPjirPvQmlayE4xlndrXTrauT6hHKT/exec";

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear any previous status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
      setDebugInfo("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setDebugInfo("");

    try {
      // Log the data being sent for debugging
      console.log("Submitting form data:", formData);

      // Create form data for Google Apps Script
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("subject", formData.subject);
      formDataToSend.append("message", formData.message);
      formDataToSend.append("timestamp", new Date().toISOString());

      // Log FormData contents
      console.log("FormData contents:");
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, value);
      }

      // Try with different approaches
      let response;
      let responseText = "";

      try {
        // First attempt: Try with cors mode to get response
        response = await fetch(GOOGLE_SCRIPT_URL, {
          method: "POST",
          body: formDataToSend,
          mode: "cors",
        });

        if (response.ok) {
          responseText = await response.text();
          console.log("Response text:", responseText);
        }
      } catch (corsError) {
        console.log("CORS request failed, trying no-cors mode:", corsError);

        // Fallback: Use no-cors mode
        response = await fetch(GOOGLE_SCRIPT_URL, {
          method: "POST",
          body: formDataToSend,
          mode: "no-cors",
        });
      }

      // Alternative approach: Try with URL-encoded data
      if (!response || !response.ok) {
        console.log("Trying with URL-encoded data...");

        const urlEncodedData = new URLSearchParams();
        urlEncodedData.append("name", formData.name);
        urlEncodedData.append("email", formData.email);
        urlEncodedData.append("phone", formData.phone);
        urlEncodedData.append("subject", formData.subject);
        urlEncodedData.append("message", formData.message);
        urlEncodedData.append("timestamp", new Date().toISOString());

        response = await fetch(GOOGLE_SCRIPT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: urlEncodedData,
          mode: "no-cors",
        });
      }

      // Since we might be using no-cors mode, we can't always read the response
      // But if we reach here without error, it likely succeeded
      setSubmitStatus("success");
      setDebugInfo(`Form submitted at ${new Date().toLocaleTimeString()}`);

      // Reset form on success
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      console.log("Form submitted successfully to Google Sheets");

      // Additional debugging: Try to ping the script URL to verify it's accessible
      setTimeout(async () => {
        try {
          const testResponse = await fetch(GOOGLE_SCRIPT_URL, {
            method: "GET",
            mode: "no-cors",
          });
          console.log("Script URL is accessible");
        } catch (testError) {
          console.log("Script URL test failed:", testError);
        }
      }, 1000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
      setDebugInfo(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="relative w-full overflow-hidden pt-[120px] pb-[100px] bg-white font-['Poppins',sans-serif]"
      role="banner"
      aria-label="Contact us hero section"
    >
      {/* Background System */}
      <div className="absolute inset-0 w-full h-full">
        {/* Base Gradient */}
        <div
          className="absolute top-0 left-0 h-full w-full bg-gradient-to-br from-[#1719A8] to-[#0F1280]"
          aria-hidden="true"
        />

        {/* Animated Wave Layers */}
        <svg
          className="absolute top-0 left-0 h-full w-full opacity-40"
          viewBox="0 0 1200 800"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient
              id="waveGradient1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.1" />
              <stop offset="50%" stopColor="#D96F2B" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#C9C51A" stopOpacity="0.03" />
            </linearGradient>
          </defs>
          <path
            d="M0,0 L1200,0 L1200,400 C1100,450 1000,500 900,520 C700,560 500,580 300,570 C150,565 75,560 0,550 Z"
            fill="url(#waveGradient1)"
            className="animate-pulse"
          />
        </svg>

        {/* Floating Communication Icons */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <div
            className="absolute top-20 left-10 w-8 h-8 bg-white/20 rounded-lg animate-float flex items-center justify-center"
            style={{ animationDelay: "0s" }}
          >
            <MessageCircle size={16} className="text-white/60" />
          </div>
          <div
            className="absolute top-40 right-20 w-6 h-6 bg-[#D96F2B]/30 rounded-lg animate-float flex items-center justify-center"
            style={{ animationDelay: "2s" }}
          >
            <Phone size={12} className="text-white/60" />
          </div>
          <div
            className="absolute bottom-40 left-1/4 w-10 h-10 bg-[#C9C51A]/20 rounded-lg animate-float flex items-center justify-center"
            style={{ animationDelay: "4s" }}
          >
            <Mail size={20} className="text-white/60" />
          </div>
          <div
            className="absolute bottom-20 right-1/3 w-7 h-7 bg-white/15 rounded-lg animate-float flex items-center justify-center"
            style={{ animationDelay: "1s" }}
          >
            <MapPin size={14} className="text-white/60" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 mx-auto px-4 max-w-7xl">
        <div className="flex flex-col lg:flex-row items-start gap-[80px]">
          {/* LEFT CONTENT - 60% */}
          <div
            className={`w-full lg:w-[60%] transition-all duration-1000 ease-out ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            {/* Contact Badge */}
            <div className="inline-flex items-center px-6 py-3 bg-white/20 backdrop-blur-md border border-white/30 rounded-full mb-6 shadow-lg">
              <span className="w-3 h-3 bg-[#C9C51A] rounded-full mr-3 animate-pulse"></span>
              <span className="text-white/90 text-sm font-semibold uppercase tracking-wider">
                Let's Start a Conversation
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.1] text-white mb-6 font-['Poppins',sans-serif]">
              Get In{" "}
              <span className="relative bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] bg-clip-text text-transparent">
                Touch
              </span>
            </h1>

            {/* Tagline */}
            <div className="mb-8">
              <div className="text-2xl lg:text-3xl font-bold text-white/90 mb-4 leading-tight">
                We're Here to Help • Ready to Listen • Quick to Respond
              </div>
              <p className="text-lg font-normal leading-[1.7] text-white/80 max-w-[650px]">
                Have a question, need support, or want to discuss your next
                project? Our team is ready to assist you. Reach out through the
                form or use any of our contact methods. We respond to all
                inquiries within 24 hours and are committed to providing
                exceptional customer service.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+16786790899">
                <button
                  className="px-10 py-4 text-lg font-bold text-[#1719A8] bg-white border-none rounded-full cursor-pointer transition-all duration-300 font-['Poppins',sans-serif] min-w-[220px] hover:bg-gray-100 hover:scale-105 shadow-xl"
                  aria-label="Call us now"
                >
                  Call Us Now
                </button>
              </a>
            </div>
          </div>

          {/* RIGHT CONTENT - 40% - Contact Form */}
          <div
            className={`w-full lg:w-[40%] transition-all duration-1000 ease-out delay-300 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-8 opacity-0"
            }`}
          >
            {/* Contact Form */}
            <div className="relative w-full max-w-md mx-auto">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-4 md:p-8 shadow-2xl">
                {/* Form Header */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-white/80 text-sm">
                    We'll get back to you within 24 hours
                  </p>
                </div>

                {/* Success/Error Messages */}
                {submitStatus === "success" && (
                  <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-xl flex items-center gap-2 text-green-300">
                    <CheckCircle size={18} />
                    <div>
                      <div className="text-sm font-medium">
                        Message sent successfully! We'll be in touch soon.
                      </div>
                      {debugInfo && (
                        <div className="text-xs text-green-200 mt-1">
                          {debugInfo}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl flex items-center gap-2 text-red-300">
                    <AlertCircle size={18} />
                    <div>
                      <div className="text-sm font-medium">
                        Failed to send message. Please try again or contact us
                        directly.
                      </div>
                      {debugInfo && (
                        <div className="text-xs text-red-200 mt-1">
                          {debugInfo}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
                      <User size={18} />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Full Name"
                      required
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Your full name"
                    />
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
                      <Mail size={18} />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Your Email Address"
                      required
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Your email address"
                    />
                  </div>

                  {/* Phone Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
                      <Phone size={18} />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Your Phone Number"
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Your phone number"
                    />
                  </div>

                  {/* Subject Field */}
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60">
                      <MessageSquare size={18} />
                    </div>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Subject"
                      required
                      disabled={isSubmitting}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Message subject"
                    />
                  </div>

                  {/* Message Field */}
                  <div className="relative">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us how we can help you..."
                      required
                      rows={4}
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#C9C51A] focus:border-transparent transition-all duration-300 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Your message"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 px-6 rounded-xl font-semibold text-[#1719A8] bg-gradient-to-r from-[#C9C51A] to-[#D96F2B] hover:from-[#D96F2B] hover:to-[#C9C51A] transition-all duration-300 transform hover:scale-105 shadow-lg ${
                      isSubmitting
                        ? "opacity-70 cursor-not-allowed transform-none"
                        : ""
                    }`}
                    aria-label="Send your message"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-[#1719A8] border-t-transparent rounded-full animate-spin"></div>
                        Sending Message...
                      </div>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>

                {/* Instructions for Google Apps Script Setup */}
                <div className="mt-4 text-center">
                  <p className="text-white/60 text-xs">
                    ⚡ Powered by Google Sheets integration
                  </p>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#C9C51A]/80 rounded-full animate-pulse"></div>
                <div
                  className="absolute -bottom-2 -left-2 w-4 h-4 bg-[#D96F2B]/80 rounded-full animate-pulse"
                  style={{ animationDelay: "1s" }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(2deg);
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}

export default ContactHero;
